/**
 * A entrada do "Próximos dias" e o painel dele (`docs/proposta-previsao-entrada.md`,
 * pedido do usuário de 17/10/2026): a previsão é MODELO, não mais uma camada
 * observada — sai do trilho e ganha entrada própria, à direita, como os
 * "Últimos focos" e o rastreio.
 *
 * - Desktop: a aba "Próximos dias" na pilha das abas da direita (App
 *   `AbasDaDireita`). Tocar só troca para a previsão — sem abrir janela
 *   nenhuma (pedido do usuário, 21/10/2026: abrir os ajustes a cada entrada
 *   incomodava). Tocar de novo volta ao observado (18/10/2026). A ficha do
 *   ponto abre no CARTÃO (no lugar dos cartões do rastreio e dos focos); a
 *   opacidade e os ajustes, no painel lateral à esquerda, pela seta dos botões
 *   da previsão no trilho — como as camadas observadas (PainelLateralDaPrevisao).
 * - Celular: um botão violeta na coluna da direita (abaixo dos focos; no modo,
 *   que os oculta, no lugar deles). Entra e sai do modo, como a aba; a FOLHA
 *   (ajustes ou ponto) abre pelo "⚙" do trilho e pelo toque no mapa, sobre o
 *   mapa, acima da barra — sem travar o mapa: tocar noutro ponto troca a ficha.
 * - O ponto escolhido fica à vista: se cair debaixo do cartão ou da folha, o
 *   mapa anda até a parte livre.
 */
import clsx from 'clsx';
import { CalendarDays, ChevronLeft, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { useExportLock } from '../loop/playback';
import { useMapInstance } from '../map/mapContext';
import { formatLonLat } from '../lib/format';
import { lonLatToMap } from '../map/projection';
import { painelSobreOMapa } from '../map/viewControl';
import { showToast } from '../state/toasts';
import { useModalLayer } from '../ui/modalStack';
import { marcarVisto, useEhNovidade } from '../ui/novidade';
import { useDeitado } from '../ui/useMediaQuery';
import { BOTAO_DA_COLUNA_PX, VAO_DA_COLUNA_PX } from '../ui/layoutCelular';
import { AjustesDaPrevisao } from './AjustesDaPrevisao';
import { BASE_PREVISAO, ladoDaCelulaKm } from './api';
import { FichaDoPonto } from './FichaDoPonto';
import { abrirPainelLateral, recolherObservadasAoEntrar } from './PainelLateralDaPrevisao';
import { nomeDoFuso } from './ponto';
import { definirModo } from './motor';
import { tempoEmUso, usePrevisaoStore } from './store';
import type OlMap from 'ol/Map';

/** Selo de novidade da entrada (o mesmo prazo que o painel tinha no trilho). */
const NOVIDADE = { id: 'previsao', ate: '2026-12-31' } as const;

/** Há previsão para mostrar (servidor configurado e alguma rodada no índice)? */
function useDisponivel(): boolean {
  return usePrevisaoStore((s) => !!BASE_PREVISAO && !!s.indice && Object.values(s.indice.modelos).some((m) => m && m.rodadas.length > 0));
}

/** Entra no modo, sem abrir painel nenhum (a aba e o "Próximos dias ▸" da barra das 48 h). */
export function entrarNaPrevisao(): void {
  marcarVisto(NOVIDADE.id);
  definirModo(true);
  if (usePrevisaoStore.getState().modo) recolherObservadasAoEntrar();
}

// ---------------------------------------------------------------------------
// Desktop

/** A aba vertical, na pilha da direita. */
export function AbaDaPrevisao() {
  const disponivel = useDisponivel();
  const modo = usePrevisaoStore((s) => s.modo);
  const painel = usePrevisaoStore((s) => s.painel);
  const travado = useExportLock();
  const novidade = useEhNovidade(NOVIDADE.id, NOVIDADE.ate);
  const lateral = usePrevisaoStore((s) => s.lateral);
  if (!disponivel) return null;
  // No modo a aba fica no lugar e volta ao observado (pedido do usuário, 18/10/2026).
  // Destacada com algo da previsão aberto no desktop: a ficha (cartão) ou o painel lateral.
  const aberto = modo && (painel === 'ponto' || lateral);
  return (
    <button
      type="button"
      onClick={() => (modo ? definirModo(false) : entrarNaPrevisao())}
      disabled={travado && !modo}
      aria-label={modo ? 'Sair da previsão e voltar ao observado' : 'Ver a previsão dos próximos dias (ECMWF e CAMS)'}
      aria-pressed={modo}
      data-aba-previsao=""
      title={travado && !modo ? 'Indisponível durante a exportação' : modo ? 'Voltar ao observado' : undefined}
      className={clsx(
        'relative flex h-40 min-h-28 w-7 shrink items-center justify-center rounded-l-xl border border-r-0 bg-ink-900/95 text-ink-100 shadow-lg hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-violet-400 disabled:opacity-40',
        modo ? 'border-violet-400/80' : 'border-ink-600/90',
        aberto && 'bg-violet-950/95',
      )}
    >
      <span className="flex rotate-180 items-center gap-1.5 text-[11px] font-medium whitespace-nowrap [writing-mode:vertical-rl]">
        <CalendarDays className="size-3.5 rotate-90 text-violet-300" aria-hidden />
        Próximos dias
        <span className={clsx('size-1.5 rounded-full', modo ? 'bg-violet-400' : 'bg-ink-500')} aria-hidden />
      </span>
      {novidade && <span className="absolute top-1.5 left-1.5 size-1.5 rounded-full bg-violet-400 ring-2 ring-ink-900" aria-hidden />}
    </button>
  );
}

/** Largura do cartão (px): 7 dias da ficha lado a lado, com "Muito ruim" inteiro. Fixa — trocar de vista não mexe no layout. */
export const LARGURA_DO_CARTAO = 640;

/** O cartão da previsão (desktop), no lugar dos cartões do rastreio e dos focos. */
export function CartaoDaPrevisao() {
  const map = useMapInstance((s) => s.map);
  const modo = usePrevisaoStore((s) => s.modo);
  const painel = usePrevisaoStore((s) => s.painel);
  const ref = useRef<HTMLElement>(null);
  useRevelarPonto(map, ref, 'direita');
  // No desktop o cartão é só da ficha do ponto: os ajustes ficam no painel lateral.
  const naFicha = painel === 'ponto';
  useEffect(() => {
    if (naFicha) ref.current?.focus({ preventScroll: true });
  }, [naFicha]);
  if (!modo || !naFicha) return null;
  return (
    <aside
      ref={ref}
      tabIndex={-1}
      aria-label="Próximos dias"
      data-painel-previsao=""
      className="absolute top-[60px] right-14 z-20 flex max-h-[calc(100%-136px)] flex-col overflow-hidden rounded-xl border border-violet-500/50 bg-ink-900/95 shadow-2xl outline-none"
      style={{ width: `min(${LARGURA_DO_CARTAO}px, calc(100% - 72px))` }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') usePrevisaoStore.getState().fecharPainel();
      }}
    >
      <Cabecalho desktop />
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 [--borda:0.75rem] [scrollbar-gutter:stable]">
        <Corpo />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Comum

/**
 * Uma linha: nos ajustes, o título; na ficha, "‹" (volta aos ajustes), o NOME
 * do lugar e ✕ (rodada 2 da proposta: a linha "‹ Ajustes da previsão" sozinha
 * e o nome numa segunda linha tiravam o gráfico da primeira vista no celular).
 */
function Cabecalho(props: { desktop?: boolean }) {
  const painel = usePrevisaoStore((s) => s.painel);
  const lateralAberto = usePrevisaoStore((s) => s.lateral);
  const ponto = usePrevisaoStore((s) => s.ponto);
  const lugar = usePrevisaoStore((s) => s.lugarDoPonto);
  const lado = usePrevisaoStore((s) => ladoDaCelulaKm(s.indice?.modelos[tempoEmUso(s)]?.grade.passo ?? 0.25));
  // A folha alta cobre o selo do modo: o aviso vem para cá.
  const alta = usePrevisaoStore((s) => s.encaixe === 'alta');
  const { abrirAjustes, fecharPainel } = usePrevisaoStore.getState();
  const naFicha = painel === 'ponto' && !!ponto;
  return (
    // Na ficha, sem divisória: a faixa dos dias, logo abaixo, tem a sua.
    <header className={clsx('flex min-h-11 items-center gap-0.5 py-1 pr-1 pl-0.5', !naFicha && 'border-b border-ink-700/70')}>
      {naFicha && props.desktop ? (
        // Desktop: os ajustes ficam no painel lateral, e a ficha continua aberta ao lado dele.
        <button
          type="button"
          onClick={() => abrirPainelLateral(!usePrevisaoStore.getState().lateral)}
          aria-pressed={lateralAberto}
          aria-label="Ajustes da previsão (opacidade e legenda), no painel à esquerda"
          title="Ajustes da previsão"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-violet-200 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-violet-400"
        >
          <SlidersHorizontal className="size-4" aria-hidden />
        </button>
      ) : naFicha ? (
        <button
          type="button"
          onClick={abrirAjustes}
          aria-label="Voltar aos ajustes da previsão"
          title="Ajustes da previsão"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-violet-200 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-violet-400 pointer-coarse:size-11"
        >
          <ChevronLeft className="size-5" aria-hidden />
        </button>
      ) : (
        <CalendarDays className="mx-2 size-4 shrink-0 text-violet-300" aria-hidden />
      )}
      {naFicha ? (
        // O nome e, embaixo, as coordenadas (com a hora do lugar, fora de Brasília) — um bloco só.
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base leading-tight font-semibold text-white" data-nome-do-ponto="">
            {lugar?.nome ?? formatLonLat(ponto[0], ponto[1])}
            {lugar?.uf && <span className="font-normal text-ink-300">/{lugar.uf}</span>}
          </h2>
          <p className="truncate text-[11px] leading-4 text-ink-300 pointer-coarse:text-xs" title={`${formatLonLat(ponto[0], ponto[1])} · média de uma área de ${lado}`}>
            {alta && <span className="font-semibold text-violet-300">Modelo, não medição · </span>}
            {formatLonLat(ponto[0], ponto[1])}
            {lugar && lugar.fuso !== -3 && <> · hora de {nomeDoFuso(lugar.fuso)}</>}
          </p>
        </div>
      ) : (
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-100">Ajustes da previsão</h2>
      )}
      <button
        type="button"
        onClick={fecharPainel}
        aria-label="Fechar (a previsão continua no mapa)"
        title="Fechar (a previsão continua no mapa)"
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-ink-300 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-violet-400 pointer-coarse:size-11"
      >
        <X className="size-4" aria-hidden />
      </button>
    </header>
  );
}

function Corpo() {
  const painel = usePrevisaoStore((s) => s.painel);
  const ponto = usePrevisaoStore((s) => s.ponto);
  return painel === 'ponto' && ponto ? (
    <FichaDoPonto lon={ponto[0]} lat={ponto[1]} />
  ) : (
    <div className="pt-3">
      <AjustesDaPrevisao />
    </div>
  );
}

/** Folga (px) do ponto até a borda da parte livre do mapa; o topo mínimo (a linha dos chips); a coluna de botões do celular. */
const FOLGA_PX = 24;
const TOPO_MIN_PX = 72;
const COLUNA_DO_CELULAR_PX = 64;

/**
 * Leva o ponto da ficha à parte do mapa que o painel não cobre, quando ele cai
 * debaixo do painel, do selo do modo (medido: com o aviso e a safe-area) ou
 * rente às bordas. Quando o ponto muda e quando o painel muda de tamanho (o
 * encaixe da folha, o giro do celular) — depois de a transição assentar.
 */
function useRevelarPonto(map: OlMap | null, painelRef: RefObject<HTMLElement | null>, lado: 'direita' | 'embaixo'): void {
  const ponto = usePrevisaoStore((s) => s.ponto);
  useEffect(() => {
    const el = painelRef.current;
    if (!map || !ponto || !el) return;
    const revelar = () => {
      map.updateSize();
      const vp = map.getViewport().getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const px = map.getPixelFromCoordinate(lonLatToMap(ponto[0], ponto[1]));
      const size = map.getSize();
      if (!px || !size) return;
      const selo = document.querySelector('[data-selo-previsao]')?.getBoundingClientRect();
      const topo = Math.max(TOPO_MIN_PX, selo && selo.height > 0 ? selo.bottom - vp.top + 16 : 0);
      // A parte livre: sem o painel da previsão (à direita ou embaixo), sem o painel lateral
      // (desktop, à esquerda) e, com a folha embaixo, sem a coluna de botões da direita.
      const esq = painelSobreOMapa();
      const livre =
        lado === 'embaixo'
          ? { esq, dir: vp.width - COLUNA_DO_CELULAR_PX, base: r.top - vp.top }
          : { esq, dir: r.left - vp.left, base: vp.height };
      // A folha alta cobre o mapa: não há onde pôr o ponto.
      if (livre.base - topo < 2 * FOLGA_PX || livre.dir - livre.esq < 2 * FOLGA_PX) return;
      const dentro = px[0] >= livre.esq + FOLGA_PX && px[0] <= livre.dir - FOLGA_PX && px[1] >= topo && px[1] <= livre.base - FOLGA_PX;
      if (dentro) return;
      const alvo = [(livre.esq + livre.dir) / 2, (topo + livre.base) / 2];
      const centro = map.getCoordinateFromPixel([size[0] / 2 + (px[0] - alvo[0]), size[1] / 2 + (px[1] - alvo[1])]);
      if (centro) map.getView().animate({ center: centro, duration: 350 });
    };
    // Agora, e de novo quando a borda do painel que limita o mapa mudar (o encaixe, o giro) — depois que ela
    // parar de mudar. A ficha carregando ou trocando de janela muda a altura do cartão, não a parte livre: não conta.
    const borda = () => {
      const r = el.getBoundingClientRect();
      return lado === 'embaixo' ? r.top : r.left;
    };
    let ultima = borda();
    let espera = 0;
    const ro = new ResizeObserver(() => {
      // Arrastando a folha: só no soltar.
      if (el.dataset.arrastando !== undefined || Math.abs(borda() - ultima) < 4) return;
      window.clearTimeout(espera);
      espera = window.setTimeout(() => {
        ultima = borda();
        revelar();
      }, 180);
    });
    revelar();
    ro.observe(el);
    return () => {
      window.clearTimeout(espera);
      ro.disconnect();
    };
  }, [map, ponto, painelRef, lado]);
}

// ---------------------------------------------------------------------------
// Celular

/** Primeira entrada na visita: avisa onde ficam os ajustes e a ficha do ponto. */
let jaAvisou = false;

/** O botão violeta da coluna da direita: entra e sai do "Próximos dias", como a aba do desktop. */
export function BotaoDaPrevisaoNaColuna() {
  const disponivel = useDisponivel();
  const modo = usePrevisaoStore((s) => s.modo);
  const travado = useExportLock();
  const novidade = useEhNovidade(NOVIDADE.id, NOVIDADE.ate);
  if (!disponivel) return null;
  // Fora do modo, o 4º da coluna (voltar, "⋯", focos); no modo os focos saem e ele sobe.
  const posicao = modo ? 2 : 3;
  const tocar = () => {
    marcarVisto(NOVIDADE.id);
    if (modo) return definirModo(false);
    definirModo(true);
    if (usePrevisaoStore.getState().modo && !jaAvisou) {
      jaAvisou = true;
      showToast({ id: 'previsao-ajustes', kind: 'info', message: 'Toque no mapa para a previsão de um ponto; o ⚙ do trilho abre os ajustes.' });
    }
  };
  return (
    <button
      type="button"
      onClick={tocar}
      disabled={travado && !modo}
      aria-pressed={modo}
      aria-label={modo ? 'Sair da previsão e voltar ao observado' : 'Entrar na previsão dos próximos dias'}
      title={modo ? 'Voltar ao observado' : 'Próximos dias (previsão)'}
      data-coluna-do-mapa=""
      data-botao-previsao-celular=""
      className={clsx(
        'absolute right-[calc(8px+env(safe-area-inset-right))] z-10 inline-flex size-11 items-center justify-center rounded-xl border shadow-lg focus-visible:outline-2 focus-visible:outline-violet-400 disabled:opacity-40',
        modo ? 'border-violet-300 bg-violet-600 text-white' : 'border-ink-600 bg-ink-900/95 text-violet-300',
      )}
      style={{ top: `calc(env(safe-area-inset-top) + 12px + ${posicao * (BOTAO_DA_COLUNA_PX + VAO_DA_COLUNA_PX)}px)` }}
    >
      <CalendarDays className="size-5" aria-hidden />
      {novidade && <span className="absolute top-1 left-1 size-1.5 rounded-full bg-violet-300 ring-2 ring-ink-900" aria-hidden />}
    </button>
  );
}

/** Alturas da folha em pé: a média (o mapa e o ponto à vista) e a alta (até logo abaixo da linha do topo). */
const ALTURA_DO_ENCAIXE = { media: '60%', alta: 'calc(100% - env(safe-area-inset-top) - 64px)' } as const;
/** Quanto (px) arrastar a alça para trocar de encaixe. */
const ARRASTO_PX = 40;

/**
 * A folha da previsão (celular): em pé, embaixo, sobre o mapa, em dois
 * encaixes (alça e cabeçalho arrastam; tocar na alça alterna); deitado, à
 * direita, inteira. Não trava o mapa.
 */
export function FolhaDaPrevisao() {
  const map = useMapInstance((s) => s.map);
  const modo = usePrevisaoStore((s) => s.modo);
  const painel = usePrevisaoStore((s) => s.painel);
  const encaixe = usePrevisaoStore((s) => s.encaixe);
  const deitado = useDeitado();
  const ref = useRef<HTMLDivElement>(null);
  // O arrasto mexe na altura direto no elemento (sem redesenhar a ficha a cada movimento).
  const inicio = useRef<{ y: number; naAlca: boolean; arrastando: boolean } | null>(null);
  const aberta = modo && !!painel;
  useModalLayer(aberta, { modal: false, onEscape: () => usePrevisaoStore.getState().fecharPainel(), ref });
  useRevelarPonto(map, ref, deitado ? 'direita' : 'embaixo');
  useEffect(() => {
    if (aberta) ref.current?.focus({ preventScroll: true });
  }, [aberta]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.style.height = deitado ? '' : ALTURA_DO_ENCAIXE[encaixe];
  }, [aberta, deitado, encaixe]);
  // O pé do mapa (Voltar · relógio · play, BarraDaPrevisao `PeDaPrevisao`) fica à vista com a folha
  // aberta: em pé ele sobe para cima dela; deitado, encolhe à esquerda dela.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!aberta || !el) return;
    const raiz = document.documentElement;
    const medir = () => {
      const r = el.getBoundingClientRect();
      raiz.style.setProperty('--folha-previsao-altura', deitado ? '0px' : `${Math.ceil(r.height)}px`);
      raiz.style.setProperty('--folha-previsao-largura', deitado ? `${Math.ceil(r.width)}px` : '0px');
    };
    medir();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      raiz.style.removeProperty('--folha-previsao-altura');
      raiz.style.removeProperty('--folha-previsao-largura');
    };
  }, [aberta, deitado]);
  if (!aberta) return null;

  const alternar = () => usePrevisaoStore.getState().setEncaixe(encaixe === 'alta' ? 'media' : 'alta');
  const soltar = (dy: number) => {
    const el = ref.current;
    if (el) {
      el.style.transition = '';
      delete el.dataset.arrastando;
      // Volta à altura do encaixe (o mesmo ou o novo, que o efeito acima também aplica).
      el.style.height = ALTURA_DO_ENCAIXE[encaixe];
    }
    const s = usePrevisaoStore.getState();
    if (dy <= -ARRASTO_PX) s.setEncaixe('alta');
    else if (dy >= ARRASTO_PX) {
      if (encaixe === 'alta') s.setEncaixe('media');
      else s.fecharPainel();
    }
  };
  // Só a alça e o cabeçalho arrastam (não os botões dele, nem o conteúdo, que rola).
  const arrastar = deitado
    ? {}
    : {
        onPointerDown: (e: ReactPointerEvent) => {
          if ((e.target as HTMLElement).closest('button:not([data-alca-da-folha])')) return;
          inicio.current = { y: e.clientY, naAlca: !!(e.target as HTMLElement).closest('[data-alca-da-folha]'), arrastando: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        },
        onPointerMove: (e: ReactPointerEvent) => {
          if (inicio.current === null) return;
          const dy = e.clientY - inicio.current.y;
          const el = ref.current;
          if (!el || (!inicio.current.arrastando && Math.abs(dy) <= 6)) return;
          inicio.current.arrastando = true;
          el.dataset.arrastando = '';
          el.style.transition = 'none';
          el.style.height = `max(96px, min(calc(${ALTURA_DO_ENCAIXE[encaixe]} - ${dy}px), ${ALTURA_DO_ENCAIXE.alta}))`;
        },
        onPointerUp: (e: ReactPointerEvent) => {
          if (inicio.current === null) return;
          const dy = e.clientY - inicio.current.y;
          const { naAlca, arrastando } = inicio.current;
          inicio.current = null;
          // Com o ponteiro capturado, o clique não chega à alça: o toque nela alterna aqui.
          if (arrastando) soltar(dy);
          else if (naAlca) alternar();
        },
        onPointerCancel: () => {
          inicio.current = null;
          soltar(0);
        },
      };
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Próximos dias"
      tabIndex={-1}
      data-painel-previsao=""
      data-encaixe={deitado ? undefined : encaixe}
      className={clsx(
        'absolute z-30 flex flex-col border-violet-500/50 bg-ink-900 shadow-2xl outline-none',
        deitado
          ? 'inset-y-0 right-0 w-[min(calc(420px+env(safe-area-inset-right)),62%)] rounded-l-2xl border-l pr-[env(safe-area-inset-right)]'
          : 'inset-x-0 bottom-0 rounded-t-2xl border-t',
        !deitado && 'transition-[height] duration-200 ease-out',
      )}
    >
      {/* Opaco e acima do conteúdo: o que rola por baixo não aparece na borda do cabeçalho. */}
      <div className={clsx('relative z-20 rounded-t-2xl bg-ink-900', !deitado && 'touch-none pt-2.5')} {...arrastar}>
        {!deitado && (
          <button
            type="button"
            data-alca-da-folha=""
            // Teclado (Enter/espaço); o toque é tratado no soltar do arrasto.
            onClick={(e) => e.detail === 0 && alternar()}
            aria-label={encaixe === 'alta' ? 'Baixar a folha (mais mapa)' : 'Subir a folha (a ficha inteira)'}
            aria-expanded={encaixe === 'alta'}
            className="absolute inset-x-0 top-0 mx-auto flex h-5 w-20 items-start justify-center pt-1.5 focus-visible:outline-2 focus-visible:outline-violet-400"
          >
            <span className="h-1 w-9 rounded-full bg-white/30" aria-hidden />
          </button>
        )}
        <Cabecalho />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 [--borda:1rem] [scrollbar-gutter:stable]">
        <Corpo />
      </div>
    </div>
  );
}
